import React, { useMemo } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, AreaChart, Area,
    Cell
} from "recharts";
import {
    Calculator, Activity, Scale, AlertCircle,
    Building, Users, Package, Tag,
    ShoppingBag, Truck, FileText, Shield
} from 'lucide-react';

const Expenses = ({ isMobile, colors, generateExpenseData, generateExpenseTrendData }) => {

    // Format number with Indian comma separators
    const formatRupees = (amount) => {
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    // Expense Breakdown
    const ExpenseBreakdown = () => {
        const expenseData = generateExpenseData;
        const trendData = generateExpenseTrendData;

        return (
            <div style={{
                background: 'white',
                borderRadius: '24px',
                padding: isMobile ? '20px' : '28px',
                marginBottom: isMobile ? '20px' : '28px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 12px 32px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #ef4444, #f59e0b)'
                }}></div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '12px' : '0' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white'
                            }}>
                                <Calculator size={24} />
                            </div>
                            <div>
                                <h3 style={{
                                    fontSize: isMobile ? '18px' : '22px',
                                    fontWeight: '800',
                                    color: '#1e293b',
                                    marginBottom: '2px'
                                }}>
                                    Expense Breakdown
                                </h3>
                                <p style={{
                                    fontSize: isMobile ? '13px' : '15px',
                                    color: '#64748b',
                                    lineHeight: 1.3
                                }}>
                                    Detailed cost analysis by category
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ marginBottom: '32px' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', marginBottom: '16px' }}>
                        Expense Trend Over Time
                    </h4>
                    <div style={{ height: isMobile ? '240px' : '320px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis
                                    dataKey="date"
                                    fontSize={isMobile ? 11 : 12}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b' }}
                                />
                                <YAxis
                                    fontSize={isMobile ? 11 : 12}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b' }}
                                    tickFormatter={(value) => formatRupees(value)}
                                />
                                <Tooltip formatter={(value) => formatRupees(value)} />
                                <Area type="monotone" dataKey="totalExpenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} name="Total Expenses" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: isMobile ? '24px' : '32px'
                }}>
                    <div style={{ height: isMobile ? '320px' : '480px' }}>
                        <h4 style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', marginBottom: '16px' }}>
                            Expense Distribution
                        </h4>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={expenseData.expenses} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={true} />
                                <XAxis
                                    type="number"
                                    fontSize={isMobile ? 11 : 12}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b' }}
                                    tickFormatter={(value) => formatRupees(value)}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="category"
                                    width={isMobile ? 100 : 140}
                                    fontSize={isMobile ? 11 : 12}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#64748b' }}
                                />
                                <Tooltip formatter={(value) => formatRupees(value)} />
                                <Bar dataKey="amount" name="Amount" radius={[0, 8, 8, 0]}>
                                    {expenseData.expenses.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div>
                        <h4 style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', marginBottom: '16px' }}>
                            Expense Categories
                        </h4>
                        <div style={{
                            maxHeight: isMobile ? '320px' : '480px',
                            overflowY: 'auto',
                            paddingRight: '8px'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {expenseData.expenses.map((expense, index) => (
                                    <div key={index} style={{
                                        padding: '20px',
                                        background: '#f8fafc',
                                        borderRadius: '16px',
                                        borderLeft: `4px solid ${expense.color}`,
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.transform = 'translateX(4px)'}
                                        onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                            <div style={{
                                                width: '44px',
                                                height: '44px',
                                                borderRadius: '10px',
                                                background: `${expense.color}20`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: expense.color,
                                                flexShrink: 0
                                            }}>
                                                {React.cloneElement(expense.icon, { size: 20 })}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                                                    {expense.category}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                    {expense.description}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b' }}>
                                                    {expense.percentage}%
                                                </div>
                                                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '600' }}>
                                                    {formatRupees(expense.amount)}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                                            {expense.details}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Summary Cards for Expenses view
    const SummaryCards = () => {
        const revenueData = generateExpenseTrendData;
        const totalExpenses = revenueData.reduce((sum, item) => sum + item.totalExpenses, 0);
        const avgDailyExpense = Math.round(totalExpenses / revenueData.length);
        const totalRevenue = revenueData.reduce((sum, item) => sum + item.revenue, 0);
        const expenseRatio = totalRevenue > 0 ? (totalExpenses / totalRevenue) * 100 : 0;
        const wasteExpiry = Math.round(totalExpenses * 0.03);

        const cards = [
            {
                title: 'Total Expenses',
                value: formatRupees(totalExpenses),
                icon: <Calculator size={isMobile ? 16 : 18} />,
                color: '#ef4444',
                trend: -8.2,
                bgColor: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
            },
            {
                title: 'Avg Daily Expense',
                value: formatRupees(avgDailyExpense),
                icon: <Activity size={isMobile ? 16 : 18} />,
                color: '#f59e0b',
                trend: 2.7,
                bgColor: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
            },
            {
                title: 'Expense Ratio',
                value: `${expenseRatio.toFixed(0)}%`,
                icon: <Scale size={isMobile ? 16 : 18} />,
                color: '#3b82f6',
                trend: -1.3,
                bgColor: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
            },
            {
                title: 'Waste & Expiry',
                value: formatRupees(wasteExpiry),
                icon: <AlertCircle size={isMobile ? 16 : 18} />,
                color: '#8b5cf6',
                trend: -15.4,
                bgColor: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
            }
        ];

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: isMobile ? '8px' : '12px',
                marginBottom: isMobile ? '16px' : '20px'
            }}>
                {cards.map((card, index) => (
                    <div key={index} style={{
                        background: card.bgColor,
                        borderRadius: '12px',
                        padding: isMobile ? '12px' : '16px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                        border: '1px solid white',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer'
                    }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        <div style={{
                            position: 'absolute',
                            top: '-12px',
                            right: '-12px',
                            width: '40px',
                            height: '40px',
                            background: `${card.color}15`,
                            borderRadius: '50%'
                        }}></div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', position: 'relative', zIndex: 1 }}>
                            <div style={{ flex: 1 }}>
                                <p style={{
                                    fontSize: isMobile ? '10px' : '11px',
                                    color: '#475569',
                                    marginBottom: '4px',
                                    fontWeight: '600',
                                    letterSpacing: '0.3px'
                                }}>{card.title}</p>
                                <p style={{
                                    fontSize: isMobile ? '16px' : '20px',
                                    fontWeight: '800',
                                    color: '#1e293b',
                                    lineHeight: 1.2
                                }}>{card.value}</p>
                            </div>
                            <div style={{
                                width: isMobile ? '32px' : '40px',
                                height: isMobile ? '32px' : '40px',
                                borderRadius: '10px',
                                background: 'rgba(255, 255, 255, 0.5)',
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(255, 255, 255, 0.6)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: card.color,
                                flexShrink: 0,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                                {card.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <SummaryCards />
            <ExpenseBreakdown />
        </>
    );
};

export default Expenses;